import Footer from '../components/Footer';
import Header from '../components/Header';
import Contact from '../components/Contact';
import SeoHelmet from '../components/SeoHelmet';
import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const Articles = () => {
  const { currentLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('news'); // 'news' or 'articles'

  useEffect(() => {
    loadArticles();
  }, [currentLanguage, activeTab]);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/articles?language=${currentLanguage}&category=${activeTab === 'news' ? 'news' : 'article'}`);
      setArticles(response.data);
    } catch (error) {
      console.error('Error loading articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(currentLanguage === 'RU' ? 'ru-RU' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
    <>
      <Header />
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-xl text-gray-600">{t('loading') || 'Загрузка...'}</div>
      </div>
        <Footer />
    </>
  );
  }

  return (
    <>
      <SeoHelmet
        title={currentLanguage === 'EN' ? 'News and Articles | VELOX HINT' : 'Новости и статьи | ВЕЛОКС ХИНТ'}
        description={currentLanguage === 'EN' ? 'Latest news and articles about customs clearance, certification and licensing.' : 'Актуальные новости и полезные статьи о сертификации, таможне и ВЭД.'}
      />
      <Header />
    <div className="min-h-screen bg-gray-50 py-16">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {currentLanguage === 'RU' ? 'Новости и статьи' : 'News and Articles'}
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            {currentLanguage === 'RU' 
              ? 'Актуальные новости и полезные статьи о сертификации и таможне'
              : 'Latest news and useful articles about certification and customs'}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-lg p-1 shadow-sm">
            <button
              onClick={() => setActiveTab('news')}
              className={`px-6 py-3 rounded-md font-medium transition ${
                activeTab === 'news'
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {currentLanguage === 'RU' ? 'Новости' : 'News'}
            </button>
            <button
              onClick={() => setActiveTab('articles')}
              className={`px-6 py-3 rounded-md font-medium transition ${
                activeTab === 'articles'
                  ? 'bg-orange-500 text-white'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {currentLanguage === 'RU' ? 'Статьи' : 'Articles'}
            </button>
          </div>
        </div>

        {/* Articles Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {articles.map((article) => (
            <div
              key={article.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-lg transition overflow-hidden cursor-pointer group"
              onClick={() => navigate(`/articles/${article.slug}`)}
            >
              {article.image_url && (
                <div className="aspect-video overflow-hidden">
                  <img
                    src={article.image_url}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition duration-300"
                  />
                </div>
              )}
              <div className="p-6">
                <div className="text-sm text-gray-500 mb-2">
                  {formatDate(article.published_date)}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-orange-500 transition">
                  {article.title}
                </h3>
                <p className="text-gray-600 line-clamp-3">{article.preview}</p>
                <div className="mt-4">
                  <span className="text-orange-500 font-medium group-hover:underline">
                    {currentLanguage === 'RU' ? 'Читать далее →' : 'Read more →'}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {articles.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">
              {currentLanguage === 'RU' ? 'Статьи не найдены' : 'No articles found'}
            </p>
          </div>
        )}
      </div>
    </div>
      <Contact />
      <Footer />
    </>
  );
};

export default Articles;